import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { IEmergencia, IPopulate, IQueryParam } from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import { TablaComponent } from '../../../../../../auxiliares/tabla/tabla.component';
import {
  IColumnas,
  ICrearBoton,
  IExportarBoton,
} from '../../../../../../auxiliares/tabla/tabla.interfaces';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { DialogService } from '../../../../../../auxiliares/dialog/dialog.service';
import { EmergenciasService } from '../../../../../../auxiliares/servicios/http/emergencias.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { DescargasService } from '../../../../../navigation/descargas/service';
import {
  ESTADOS_EMERGENCIA,
  ESTADOS_FINALES,
  PRIORIDADES,
  SituacionEmergencia,
  estadoDe,
  colorTono,
  estadoCeldaEmergencia,
  estaFinalizada,
  prioridadCelda,
  situacionCelda,
  situacionDe,
} from '../../../estado-emergencia';
import { calcDiff, nombrePaciente } from '../../emergencia.service';
import {
  estadoHtml,
  fechaCelda,
  haceCelda,
} from '../../../../../../auxiliares/tabla/celdas';
import { descargarInformePdfEmergencia } from '../../informe-pdf-emergencia';
import { AccionesEmergenciaService } from '../../acciones-emergencia/acciones-emergencia.service';
import { ImportarEmergenciasComponent } from '../../importar-emergencias/importar.component';
import { CrearEditarEmergenciaComponent } from '../../crear-editar-emergencia/crear-editar-emergencia.component';
import { construirExcelEmergencias } from '../exportar-emergencias-excel';

export type SolapaEmergencias = 'auxilios' | 'seguimiento' | 'historico';

/**
 * Filtro fijo de cada solapa. Las activas excluyen las importadas (que nacen
 * finalizadas o canceladas y sólo sirven para métricas).
 */
export function filtroBaseEmergencias(
  solapa: SolapaEmergencias,
): Record<string, unknown> {
  switch (solapa) {
    case 'auxilios':
      return {
        tipo: 'Médica',
        esAuxilio: true,
        importada: { $ne: true },
        'ultimoEventoEmergencia.estado': { $nin: ESTADOS_FINALES },
      };
    case 'seguimiento':
      return {
        tipo: 'Médica',
        enSeguimiento: true,
        importada: { $ne: true },
        'ultimoEventoEmergencia.estado': { $nin: ESTADOS_FINALES },
      };
    case 'historico':
      return {
        tipo: 'Médica',
        'ultimoEventoEmergencia.estado': { $in: ESTADOS_FINALES },
      };
  }
}

/** Condición Mongo de cada situación (misma precedencia que `situacionDe`). */
const FILTRO_SITUACION: Record<SituacionEmergencia, Record<string, unknown>> = {
  Importada: { importada: true },
  Auxilio: { esAuxilio: true, importada: { $ne: true } },
  Seguimiento: {
    enSeguimiento: true,
    esAuxilio: { $ne: true },
    importada: { $ne: true },
  },
  'Registrada finalizada': {
    iniciaFinalizada: true,
    esAuxilio: { $ne: true },
    enSeguimiento: { $ne: true },
    importada: { $ne: true },
  },
  Llamada: {
    esAuxilio: { $ne: true },
    enSeguimiento: { $ne: true },
    iniciaFinalizada: { $ne: true },
    importada: { $ne: true },
  },
};

const RUTA = 'main/emergencias/emergenciasMedicas';
/** Dato con el ícono de su entidad (docs/ICONOS.md). */
const LINEA = (icono: string, contenido: string) =>
  `<div class="tabla-linea"><span class="material-symbols-outlined">${icono}</span><span>${contenido}</span></div>`;
const SECUNDARIA = (contenido: string) =>
  `<span class="tabla-secundario">${contenido}</span>`;
const NO_INFO = (texto: string) => `<span class="no-info">${texto}</span>`;

/** Escapa texto libre que se interpola en celdas html. */
function esc(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function listaCorta(nombres: string[], max = 2): string {
  const n = nombres.filter(Boolean);
  if (n.length <= max) return n.join(', ');
  return `${n.slice(0, max).join(', ')} y ${n.length - max} más`;
}

import {
  htmlDireccion,
  tooltipDireccion,
} from '../../../entidades-emergencias/presentacion-entidades';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';
@Component({
  selector: 'app-listado-emergencias-tabla',
  templateUrl: './listado-emergencias-tabla.component.html',
  imports: [TablaComponent],
})
export class ListadoEmergenciasTablaComponent implements OnInit, OnDestroy {
  public helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  private emergenciasService = inject(EmergenciasService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private descargas = inject(DescargasService);
  private acciones = inject(AccionesEmergenciaService);

  @Input({ required: true }) solapa!: SolapaEmergencias;
  /** Avisa que cambió algo (para refrescar los contadores de las solapas). */
  @Output() cambio = new EventEmitter<void>();

  public name = '';
  public datos?: IEmergencia[];
  public totalCount = 0;
  public columnas: IColumnas<IEmergencia>[] = [];
  public filtros: IFiltroTabla[] = [];
  private datos$?: Subscription;

  public get historico(): boolean {
    return this.solapa === 'historico';
  }

  public search: IRegExpSearch = {
    fields: ['direccion', 'solicitante', 'telefono', 'observaciones'],
  };

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Emergencias Médicas - Crear'),
    tooltip: 'Crear emergencia',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.abrirFormulario(),
  };

  public botonExportar: IExportarBoton = {
    mostrar: true,
    tooltip: 'Exportar a Excel',
    color: 'primary',
    tipo: 'material',
    icon: 'table_view',
    accion: () => this.exportar(),
  };

  public botonImportar: IExportarBoton = {
    mostrar: this.helper.tienePermiso('Emergencias Médicas - Importar'),
    tooltip: 'Importar emergencias',
    color: 'primary',
    tipo: 'material',
    icon: 'upload_file',
    accion: () => this.importar(),
  };

  public botonesExportar: IExportarBoton[] = [];

  private populate: IPopulate[] = [
    {
      path: 'destinatarioAsistencia',
      select: 'nombre apellido dni edad',
    },
    {
      path: 'ultimoEventoEmergencia.detallesEmergencias.movilAsignado',
      select: 'identificacion',
    },
    {
      path: 'ultimoEventoEmergencia.detallesEmergencias.chofer',
      select: 'usuario datosPersonales',
    },
    {
      path: 'ultimoEventoEmergencia.detallesEmergencias.medicos',
      select: 'nombre',
    },
    {
      path: 'ultimoEventoEmergencia.detallesEmergencias.enfermeros',
      select: 'nombre',
    },
    {
      path: 'ultimoEventoEmergencia.detallesEmergencias.hospital',
      select: 'identificacion',
    },
    { path: 'cliente', select: 'nombre' },
  ];

  public query: IQueryParam = {};

  // Filtros

  private crearFiltros(): IFiltroTabla[] {
    const prioridad = (campo: string, label: string): IFiltroTabla => ({
      elementos: PRIORIDADES.map((p) => {
        const pr = prioridadCelda(p);
        return {
          label: pr.texto,
          value: [{ [campo]: p }],
          icon: 'circle',
          iconColor: colorTono(pr.tono),
          color: colorTono(pr.tono),
        };
      }),
      filter: { field: '$or' },
      label,
      // El código de apertura se mira todo el tiempo: chips sobre la tabla
      rapido: campo === 'prioridadApertura',
      tipo: 'select',
      selectLabel: 'label',
      selectValue: 'value',
      selectColor: 'color',
      selectIcon: true,
      compararPorJson: true,
    });

    const estados = ESTADOS_EMERGENCIA.filter(
      (e) => ESTADOS_FINALES.includes(e) === this.historico,
    );
    const estado: IFiltroTabla = {
      elementos: estados.map((e) => {
        const p = estadoCeldaEmergencia(e);
        return {
          label: p.texto,
          value: [{ 'ultimoEventoEmergencia.estado': e }],
          icon: p.icono,
          iconColor: colorTono(p.tono),
        };
      }),
      filter: { field: '$or' },
      label: 'Estado',
      // En Historial son dos (Finalizada y Cancelada): chips sobre la tabla
      rapido: this.historico,
      tipo: 'select',
      selectLabel: 'label',
      selectValue: 'value',
      selectIcon: true,
      compararPorJson: true,
    };

    const movil: IFiltroTabla = {
      elementos: [],
      filter: {
        field: 'ultimoEventoEmergencia.detallesEmergencias.idMovilAsignado',
      },
      label: 'Móvil',
      tipo: 'ngselect-async',
      asyncFunction: 'vehiculos',
      searchOn: ['identificacion'],
      selectLabel: 'identificacion',
      selectValue: '_id',
    };

    const filtros: IFiltroTabla[] = [
      prioridad('prioridadApertura', 'Código de apertura'),
      estado,
      movil,
    ];

    if (this.historico) {
      const situaciones: SituacionEmergencia[] = [
        'Auxilio',
        'Seguimiento',
        'Registrada finalizada',
        'Llamada',
        'Importada',
      ];
      filtros.push(
        prioridad('prioridadCierre', 'Código de cierre'),
        {
          elementos: situaciones.map((s) => {
            const p = situacionCelda(s);
            return {
              label: s === 'Importada' ? 'Importadas' : p.texto,
              value: [FILTRO_SITUACION[s]],
              icon: p.icono,
              iconColor: colorTono(p.tono),
            };
          }),
          filter: { field: '$or' },
          label: 'Situación',
          tipo: 'select',
          selectLabel: 'label',
          selectValue: 'value',
          selectIcon: true,
          compararPorJson: true,
        },
        {
          elementos: [],
          // Campo virtual: en listar() se traduce a médicos o enfermeros
          filter: { field: '_personalSalud' },
          label: 'Personal de salud',
          tipo: 'ngselect-async',
          asyncFunction: 'personalSaluds',
          searchOn: ['nombre', 'matricula', 'dni'],
          selectLabel: 'nombre',
          selectValue: '_id',
        },
        {
          elementos: [],
          filter: { field: 'idDestinatarioAsistencia' },
          label: 'Paciente',
          tipo: 'ngselect-async',
          asyncFunction: 'destinatarioAsistencias',
          searchOn: ['nombre', 'apellido', 'dni'],
          selectLabel: 'apellido',
          selectLabel2: 'nombre',
          selectValue: '_id',
        },
        {
          filter: { field: 'fechaCreacion' },
          label: 'Fecha de creación',
          tipo: 'dateRange',
        },
      );
    }

    if (HelperService.getTipoCliente() !== 'Final') {
      filtros.push({
        filter: { field: 'idCliente' },
        label: 'Cliente',
        tipo: 'cliente-selector',
        filtroSelector: ['moduloEmergencias'],
      });
    }
    return filtros;
  }

  /**
   * Adapta el filtro que arma la tabla: el personal de salud busca en médicos
   * y enfermeros, y una búsqueda sólo con números también busca el número de
   * emergencia (que es numérico y no admite $regex).
   */
  private normalizarFiltro(filtro: any): any {
    const entradas: any[] = Array.isArray(filtro?.$and) ? filtro.$and : [];
    filtro.$and = entradas.map((entrada) => {
      if (entrada?._personalSalud) {
        const id = entrada._personalSalud;
        return {
          $or: [
            { 'ultimoEventoEmergencia.detallesEmergencias.idsMedicos': id },
            { 'ultimoEventoEmergencia.detallesEmergencias.idsEnfermeros': id },
          ],
        };
      }
      const or = entrada?.$or;
      if (Array.isArray(or) && or.some((c: any) => c?.direccion?.$regex)) {
        const valor = String(or[0]?.direccion?.$regex ?? '').trim();
        if (/^\d+$/.test(valor)) {
          return { $or: [...or, { codigo: Number(valor) }] };
        }
      }
      return entrada;
    });
    if (!filtro.$and.length) delete filtro.$and;
    return filtro;
  }

  private queryServidor(extra?: IQueryParam): IQueryParam {
    const usuario = this.normalizarFiltro(
      JSON.parse(this.query.filter || '{}'),
    );
    const condiciones: Record<string, unknown>[] = [
      filtroBaseEmergencias(this.solapa),
    ];
    if (Object.keys(usuario).length) condiciones.push(usuario);
    const query: IQueryParam = {
      ...this.query,
      filter: JSON.stringify({ $and: condiciones }),
      populate: JSON.stringify(this.populate),
      ...extra,
    };
    // Las activas se ven siempre con toda la jerarquía, igual que el tablero
    if (!this.historico) query.includeChildren = true;
    return query;
  }

  public async listar(): Promise<void> {
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .emergencias(this.queryServidor(), 15)
      .subscribe((data) => {
        if (
          HelperService.cambiaronLosDatos(this.datos, data.datos) ||
          this.totalCount !== data.totalCount
        ) {
          this.datos = data.datos;
          this.totalCount = data.totalCount;
        }
      });
  }

  private async refrescar(cambio: boolean): Promise<void> {
    if (!cambio) return;
    await this.listar();
    this.cambio.emit();
  }

  // Acciones

  public detalles(e: IEmergencia): void {
    this.router.navigate([`${RUTA}/detalles`, e._id]);
  }

  public editar(e: IEmergencia): void {
    this.abrirFormulario({ id: e._id });
  }

  /** Crear/editar en diálogo; al guardar se refresca el listado. */
  private abrirFormulario(data?: { id?: string }): void {
    const ref = this.formularios.abrir(CrearEditarEmergenciaComponent, {
      tamano: 'grande',
      data,
    });
    ref.afterClosed().subscribe((guardada) => this.refrescar(!!guardada));
  }

  public async eliminar(e: IEmergencia): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar emergencia',
      `¿Confirmás eliminar la emergencia ${e.codigo ?? ''}? También se borra su seguimiento.`,
    );
    if (!ok) return;
    try {
      await this.emergenciasService.delete(e._id!);
      this.helper.notifSuccess('Emergencia eliminada');
      await this.refrescar(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  public importar(): void {
    const ref = this.dialog.open(ImportarEmergenciasComponent, {
      width: '900px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
    });
    ref.afterClosed().subscribe((creadas) => this.refrescar(!!creadas));
  }

  public exportar(): void {
    const fecha = new Date().toISOString().slice(0, 10);
    const query = this.queryServidor({
      page: 0,
      limit: 0,
      unlimited: true,
      sort: '-fechaCreacion',
    });
    this.descargas.agregarDescargaExcel(
      `emergencias_${this.solapa}_${fecha}`,
      async () => {
        const res = await firstValueFrom(this.lista2.emergencias(query));
        return construirExcelEmergencias(res?.datos ?? []);
      },
    );
    this.helper.notifSuccess('Exportación iniciada: la vas a ver en Descargas');
  }

  // Columnas

  private setColumnas(): void {
    const columnas: IColumnas<IEmergencia>[] = [
      {
        header: { label: 'Emergencia', sort: 'codigo', sortable: true },
        row: { html: (e) => this.htmlCodigo(e) },
      },
      {
        header: { label: 'Estado' },
        row: { html: (e) => this.htmlEstado(e) },
      },
      {
        header: { label: 'Paciente' },
        row: { html: (e) => this.htmlPaciente(e) },
      },
      {
        header: { label: 'Dirección', sort: 'direccion', sortable: true },
        row: {
          html: (e) => htmlDireccion(e.direccion),
          tooltip: (e) => tooltipDireccion(e.direccion),
        },
      },
      {
        header: { label: 'Móvil y personal' },
        row: { html: (e) => this.htmlAsignacion(e) },
      },
      {
        header: { label: 'Tiempos', sort: 'fechaCreacion', sortable: true },
        row: { html: (e) => this.htmlTiempos(e) },
      },
      {
        header: {
          label: this.historico ? 'Diagnóstico' : 'Síntomas',
          hidden: !this.historico,
        },
        row: { html: (e) => this.htmlClinica(e) },
      },
      {
        header: { label: 'Solicitante', sort: 'solicitante', sortable: true },
        row: { html: (e) => this.htmlSolicitante(e) },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente', hidden: true },
        row: {
          html: (e) => this.clienteJerarquia.htmlNombre(e.cliente),
          tooltip: (e) => this.clienteJerarquia.tooltipJerarquia(e.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    const activa = (e: IEmergencia) => !estaFinalizada(e);
    columnas.push({
      header: { label: 'Acciones' },
      row: {
        // Menú: Ver detalle primero, Eliminar al final
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (e) => this.detalles(e),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (e) => this.editar(e),
            oculta: (e) => !activa(e),
            disabled: () =>
              !this.helper.tienePermiso('Emergencias Médicas - Editar'),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'assignment_ind',
            tooltip: 'Asignar móvil',
            click: async (e) => this.refrescar(await this.acciones.asignar(e)),
            oculta: (e) => !activa(e) || !!e.asignada || !e.esAuxilio,
            disabled: () =>
              !this.helper.tienePermiso('Emergencias Médicas - Editar'),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'swap_horiz',
            tooltip: 'Reasignar',
            click: async (e) =>
              this.refrescar(await this.acciones.reasignar(e)),
            oculta: (e) => !activa(e) || !e.asignada,
            disabled: () =>
              !this.helper.tienePermiso('Emergencias Médicas - Reasignar'),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'where_to_vote',
            tooltip: 'Llegó a destino',
            click: async (e) =>
              this.refrescar(await this.acciones.llegadaDestino(e)),
            oculta: (e) => !activa(e) || !e.asignada,
            disabled: (e) => !this.acciones.puedeLlegadaDestino(e),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'local_hospital',
            tooltip: 'Rumbo a hospital',
            click: async (e) =>
              this.refrescar(await this.acciones.rumboHospital(e)),
            oculta: (e) => !activa(e) || !e.asignada,
            disabled: (e) => !this.acciones.puedeRumboHospital(e),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'task_alt',
            tooltip: 'Finalizar',
            click: async (e) =>
              this.refrescar(await this.acciones.finalizar(e)),
            oculta: (e) => !activa(e),
            disabled: () =>
              !this.helper.tienePermiso('Emergencias Médicas - Finalizar'),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'close',
            tooltip: 'Cancelar emergencia',
            click: async (e) => this.refrescar(await this.acciones.cancelar(e)),
            oculta: (e) => !activa(e),
            disabled: () =>
              !this.helper.tienePermiso('Emergencias Médicas - Cancelar'),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'picture_as_pdf',
            tooltip: 'Descargar informe PDF',
            click: (e) => descargarInformePdfEmergencia(e),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (e) => this.eliminar(e),
            disabled: () =>
              !this.helper.tienePermiso('Emergencias Médicas - Eliminar'),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Celdas

  private htmlCodigo(e: IEmergencia): string {
    let h = `<div class="tabla-celda-col">`;
    h += e.codigo
      ? `<span class="tabla-principal">${e.codigo}</span>`
      : NO_INFO('Sin número');
    h += `<div class="tabla-celda-fila">`;
    h += estadoHtml(prioridadCelda(e.prioridadApertura));
    if (e.prioridadCierre && e.prioridadCierre !== e.prioridadApertura) {
      h += `<span class="material-symbols-outlined tabla-secundario" title="Código de cierre">arrow_forward</span>`;
      h += estadoHtml(prioridadCelda(e.prioridadCierre));
    }
    h += `</div>`;
    // En las activas la situación es la de la solapa: sólo suma en Historial
    if (this.historico) {
      h += `<span>${estadoHtml(situacionCelda(situacionDe(e)))}</span>`;
    }
    h += `</div>`;
    return h;
  }

  private htmlEstado(e: IEmergencia): string {
    const ev = e.ultimoEventoEmergencia;
    let h = `<div class="tabla-celda-col">`;
    h += `<span>${estadoHtml(estadoCeldaEmergencia(estadoDe(e)))}</span>`;
    const fecha = ev?.fechaCreacion;
    if (fecha) {
      h += SECUNDARIA(this.historico ? fechaCelda(fecha) : haceCelda(fecha));
    }
    if (e.cantidadReasignaciones) {
      const n = e.cantidadReasignaciones;
      h += LINEA(
        'swap_horiz',
        `${n} ${n === 1 ? 'reasignación' : 'reasignaciones'}`,
      );
    }
    h += `</div>`;
    return h;
  }

  private htmlPaciente(e: IEmergencia): string {
    const p = e.destinatarioAsistencia;
    const nombre = nombrePaciente(p);
    if (!nombre) return NO_INFO('Sin paciente');
    let h = `<div class="tabla-celda-col">`;
    h += `<span class="tabla-principal">${esc(nombre)}</span>`;
    const extra: string[] = [];
    if (p?.dni) extra.push(`DNI ${esc(p.dni)}`);
    if (p?.edad != null) extra.push(`${p.edad} años`);
    if (extra.length) h += SECUNDARIA(extra.join(' · '));
    h += `</div>`;
    return h;
  }

  private htmlAsignacion(e: IEmergencia): string {
    const det = e.ultimoEventoEmergencia?.detallesEmergencias;
    const movil = det?.movilAsignado?.identificacion;
    const chofer = det?.chofer?.datosPersonales?.nombre || det?.chofer?.usuario;
    const personal = [
      ...(det?.medicos ?? []).map((m) => m.nombre ?? ''),
      ...(det?.enfermeros ?? []).map((m) => m.nombre ?? ''),
    ].filter(Boolean);
    const hospital = det?.hospital?.identificacion;
    if (!movil && !chofer && !personal.length && !hospital) {
      return NO_INFO(
        e.enSeguimiento && !e.esAuxilio ? 'Sin móvil' : 'Sin asignar',
      );
    }
    let h = `<div class="tabla-celda-col">`;
    h += movil
      ? `<div class="tabla-linea tabla-principal"><span class="material-symbols-outlined">airport_shuttle</span><span>${esc(movil)}</span></div>`
      : NO_INFO('Sin móvil');
    if (chofer) h += LINEA('account_box', esc(chofer));
    if (personal.length) {
      h += LINEA('medical_services', esc(listaCorta(personal)));
    }
    if (hospital) h += LINEA('local_hospital', esc(hospital));
    h += `</div>`;
    return h;
  }

  private htmlTiempos(e: IEmergencia): string {
    if (!e.fechaCreacion) return NO_INFO('Sin fecha');
    let h = `<div class="tabla-celda-col">`;
    h += `<span class="tabla-fecha">${fechaCelda(e.fechaCreacion)}</span>`;
    const asignacion = calcDiff(e.fechaCreacion, e.fechaPrimeraAsignacion);
    if (asignacion) {
      h += SECUNDARIA(`Asignada a los ${asignacion}`);
    } else if (e.esAuxilio && !estaFinalizada(e)) {
      h += SECUNDARIA(`Sin asignar desde ${haceCelda(e.fechaCreacion)}`);
    }
    const llegada = calcDiff(e.fechaCreacion, e.fechaLlegadaDestino);
    if (llegada) h += SECUNDARIA(`Llegó a los ${llegada}`);
    if (this.historico) {
      const total = calcDiff(e.fechaCreacion, e.fechaFinalizacion);
      if (total) h += LINEA('timer', `Duración total ${total}`);
    }
    h += `</div>`;
    return h;
  }

  private htmlClinica(e: IEmergencia): string {
    const med = e.emergenciaMedica;
    if (this.historico) {
      const sintomas = med?.sintomasCierre ?? [];
      if (!med?.diagnostico && !sintomas.length) {
        return NO_INFO('Sin diagnóstico');
      }
      let h = `<div class="tabla-celda-col">`;
      h += med?.diagnostico
        ? `<span class="tabla-principal">${esc(med.diagnostico)}</span>`
        : NO_INFO('Sin diagnóstico');
      if (sintomas.length) h += SECUNDARIA(esc(listaCorta(sintomas, 3)));
      h += `</div>`;
      return h;
    }
    const sintomas = med?.sintomasApertura ?? [];
    return sintomas.length
      ? `<span>${esc(listaCorta(sintomas, 3))}</span>`
      : NO_INFO('Sin síntomas');
  }

  private htmlSolicitante(e: IEmergencia): string {
    if (!e.solicitante && !e.telefono) return NO_INFO('Sin solicitante');
    let h = `<div class="tabla-celda-col">`;
    h += e.solicitante
      ? `<span class="tabla-principal">${esc(e.solicitante)}</span>`
      : NO_INFO('Sin nombre');
    // Teléfono sin ícono: no está en docs/ICONOS.md
    if (e.telefono) h += SECUNDARIA(`Tel. ${esc(e.telefono)}`);
    h += `</div>`;
    return h;
  }

  // Hooks

  async ngOnInit(): Promise<void> {
    this.name = `ListadoEmergenciasMedicas-${this.solapa}`;
    this.query = {
      filter: '{}',
      sort: '-fechaCreacion',
      page: 0,
      limit: +this.helper.getPageSize(this.name),
      includeChildren: true,
    };
    this.filtros = this.crearFiltros();
    this.botonesExportar = this.historico
      ? [this.botonExportar, this.botonImportar]
      : [this.botonExportar];
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
  }
}
